import { useState, useEffect, useRef } from 'react'
import { deploySession, pickHealthyServer } from '../api.js'
import styles from './Home.module.css'

// ── ICONS ──────────────────────────────────────────────────────────────────────
const Zap = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)
const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)
const Copy = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
)
const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
)
const Globe = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
)
const Shield = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
)
const Cpu = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/>
    <line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/>
    <line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/>
    <line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/>
    <line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/>
  </svg>
)

// ── ANIMATED COUNTER ───────────────────────────────────────────────────────────
function Counter({ value }) {
  const [display, setDisplay] = useState(0)
  const prev = useRef(0)
  useEffect(() => {
    const target = Number(value) || 0
    const start = prev.current
    const diff = target - start
    if (diff === 0) return
    const duration = 800
    const startTime = performance.now()
    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      setDisplay(Math.round(start + diff * eased))
      if (p < 1) requestAnimationFrame(tick)
      else prev.current = target
    }
    requestAnimationFrame(tick)
  }, [value])
  return <>{display}</>
}

// ── TYPING ANIMATION ───────────────────────────────────────────────────────────
function TypingText({ texts }) {
  const [idx, setIdx] = useState(0)
  const [chars, setChars] = useState(0)
  const [deleting, setDeleting] = useState(false)
  useEffect(() => {
    const current = texts[idx]
    const delay = deleting
      ? chars === 0 ? 600 : 40
      : chars === current.length ? 2000 : 70
    const t = setTimeout(() => {
      if (!deleting && chars === current.length) { setDeleting(true); return }
      if (deleting && chars === 0) { setDeleting(false); setIdx((i) => (i + 1) % texts.length); return }
      setChars((c) => c + (deleting ? -1 : 1))
    }, delay)
    return () => clearTimeout(t)
  }, [chars, deleting, idx, texts])
  return (
    <span className={styles.typingWrap}>
      {texts[idx].slice(0, chars)}
      <span className={styles.cursor} />
    </span>
  )
}

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────────
export default function Home({ serverStats, totalBots, onlineCount }) {
  const [number, setNumber]     = useState('')
  const [status, setStatus]     = useState(null)
  const [result, setResult]     = useState(null)
  const [routed, setRouted]     = useState(null)
  const [copied, setCopied]     = useState(false)
  const [visible, setVisible]   = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80)
    return () => clearTimeout(t)
  }, [])

  const handleDeploy = async () => {
    const num = number.trim()
    if (!num) { inputRef.current?.focus(); return }
    setStatus('loading')
    setResult(null)
    setRouted(null)
    const srv = pickHealthyServer(serverStats)
    if (!srv) {
      setStatus('error')
      setResult({ error: 'No servers are currently online.' })
      return
    }
    setRouted(srv.server.label)
    try {
      const data = await deploySession(srv.server, num)
      setStatus('success')
      setResult(data)
    } catch (e) {
      setStatus('error')
      setResult({ error: e.message })
    }
  }

  // Copy ONLY the code value if present, otherwise full JSON
  const handleCopy = () => {
    if (!result) return
    const toCopy = result.code ? result.code : JSON.stringify(result, null, 2)
    navigator.clipboard.writeText(toCopy)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const isOnline = onlineCount > 0

  return (
    <div className={`${styles.page} ${visible ? styles.visible : ''}`}>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <div className={styles.orb1} />
          <div className={styles.orb2} />
          <div className={styles.grid} />
        </div>

        <div className={styles.heroContent}>
          <div className={styles.badge} style={{ animationDelay: '0ms' }}>
            <span className={styles.badgeDot} />
            <span>Live Network</span>
            <span className={styles.badgeSep}>·</span>
            <span className={styles.badgeCount}>{onlineCount} servers online</span>
          </div>

          <h1 className={styles.heading}>
            <span className={styles.headLine1}>Deploy bots</span>
            <span className={styles.headLine2}>
              <TypingText texts={['instantly.', 'reliably.', 'at scale.', 'globally.']} />
            </span>
          </h1>

          <p className={styles.sub}>
            Saviya-X intelligently routes your session to the fastest available server —
            no config, no downtime, no limits.
          </p>

          <div className={styles.heroActions}>
            <button className={styles.heroCta} onClick={() => inputRef.current?.focus()}>
              Start deploying <ArrowRight />
            </button>
            <div className={styles.heroMeta}>
              <span><Globe /> Global network</span>
              <span><Shield /> Zero config</span>
              <span><Cpu /> Auto-routing</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP ── */}
      <div className={styles.statsStrip}>
        {[
          { label: 'Active Bots', value: totalBots, accent: 'var(--accent)', sub: 'across all servers' },
          { label: 'Online Servers', value: onlineCount, accent: 'var(--green)', sub: `of ${serverStats.length} total` },
          { label: 'Uptime', value: isOnline ? '99.9%' : '—', accent: isOnline ? 'var(--green)' : 'var(--red)', sub: 'this month', raw: true },
          { label: 'Avg Response', value: '~120ms', accent: 'var(--accent)', sub: 'per deploy', raw: true },
        ].map((s, i) => (
          <div key={s.label} className={styles.statItem} style={{ animationDelay: `${i * 80}ms` }}>
            <div className={styles.statVal} style={{ color: s.accent }}>
              {s.raw ? s.value : <Counter value={s.value} />}
            </div>
            <div className={styles.statLabel}>{s.label}</div>
            <div className={styles.statSub}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── DEPLOY PANEL ── */}
      <div className={styles.deployWrap}>
        <div className={styles.deployCard}>
          {/* top shimmer bar */}
          <div className={styles.shimmerBar} />

          <div className={styles.deployLeft}>
            <div className={styles.deployHeader}>
              <span className={styles.deployIcon}><Zap /></span>
              <div>
                <h2 className={styles.deployTitle}>Deploy Session</h2>
                <p className={styles.deployDesc}>Enter a number — we handle the rest</p>
              </div>
            </div>

            <div className={styles.inputWrap}>
              <span className={styles.inputPrefix}>+</span>
              <input
                ref={inputRef}
                className={styles.input}
                type="tel"
                placeholder="94XXXXXXXXX"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDeploy()}
                autoComplete="off"
              />
              <button
                className={`${styles.deployBtn} ${status === 'loading' ? styles.deploying : ''}`}
                onClick={handleDeploy}
                disabled={status === 'loading' || !number.trim()}
              >
                <span className={styles.deployBtnInner}>
                  {status === 'loading' ? (
                    <><span className={styles.spinner} /> Deploying</>
                  ) : (
                    <><Zap /> Deploy</>
                  )}
                </span>
                <span className={styles.deployBtnGlow} />
              </button>
            </div>

            {/* ROUTING PILL */}
            {routed && (
              <div className={`${styles.routingPill} ${styles[status] || ''}`}>
                <span className={styles.routingDot} />
                {status === 'loading'
                  ? <>Connecting to <strong>{routed}</strong>…</>
                  : status === 'success'
                  ? <>Deployed via <strong>{routed}</strong></>
                  : <>Failed on <strong>{routed}</strong></>
                }
              </div>
            )}

            {/* RESULT */}
            {result && (
              <div className={`${styles.resultBox} ${styles[status]}`}>
                <div className={styles.resultTop}>
                  <div className={styles.resultStatus}>
                    <span className={`${styles.resultIcon} ${styles[status]}`}>
                      {status === 'success' ? <Check /> : <XIcon />}
                    </span>
                    <span className={styles.resultStatusText}>
                      {status === 'success' ? 'Session deployed' : 'Deploy failed'}
                    </span>
                  </div>
                  <button
                    className={`${styles.copyBtn} ${copied ? styles.copied : ''}`}
                    onClick={handleCopy}
                    title={result.code ? 'Copy code' : 'Copy JSON'}
                  >
                    {copied ? <><Check /> Copied!</> : <><Copy /> {result.code ? 'Copy code' : 'Copy'}</>}
                  </button>
                </div>

                <div className={styles.resultBody}>
                  {status === 'success' && result.code ? (
                    <div className={styles.codeDisplay}>
                      <div className={styles.codeLabel}>Session Code</div>
                      <div className={styles.codeValue}>{result.code}</div>
                      {result.type && (
                        <div className={styles.codeType}>
                          <span>type</span>
                          <span className={styles.codeTypeBadge}>{result.type}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <pre className={styles.jsonPre}>{JSON.stringify(result, null, 2)}</pre>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDE FEATURES */}
          <div className={styles.deployRight}>
            <div className={styles.featureList}>
              {[
                { icon: '⚡', title: 'Instant routing', desc: 'Requests reach the healthiest server in milliseconds' },
                { icon: '🛡', title: 'Auto failover', desc: 'Dead servers are skipped automatically, zero intervention' },
                { icon: '🌐', title: 'Multi-region', desc: 'Distributed across regions for low latency everywhere' },
                { icon: '📡', title: 'Live monitoring', desc: 'Every server polled every 30s — always fresh status' },
              ].map((f, i) => (
                <div key={f.title} className={styles.featureItem} style={{ animationDelay: `${300 + i * 80}ms` }}>
                  <span className={styles.featureEmoji}>{f.icon}</span>
                  <div>
                    <div className={styles.featureTitle}>{f.title}</div>
                    <div className={styles.featureDesc}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.networkMap}>
              <div className={styles.networkLabel}>Network health</div>
              <div className={styles.networkBars}>
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className={styles.networkBar}
                    style={{
                      height: `${30 + Math.sin(i * 0.8) * 20 + Math.cos(i * 1.3) * 15}%`,
                      animationDelay: `${i * 120}ms`,
                      background: isOnline ? 'var(--accent)' : 'var(--red)',
                    }}
                  />
                ))}
              </div>
              <div className={styles.networkStatus}>
                <span className={`${styles.networkDot} ${isOnline ? styles.online : styles.offline}`} />
                {isOnline ? 'All systems operational' : 'Degraded performance'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div className={styles.howSection}>
        <div className={styles.howHeader}>
          <span className={styles.howEyebrow}>Under the hood</span>
          <h3 className={styles.howTitle}>How deployment works</h3>
        </div>
        <div className={styles.howSteps}>
          {[
            { n: '01', icon: '📶', t: 'Health check', d: 'All servers are pinged in parallel every 30 seconds. Latency, uptime, and load are measured continuously.' },
            { n: '02', icon: '🎯', t: 'Smart routing', d: 'Your request is instantly dispatched to a random healthy server. Down servers are never touched.' },
            { n: '03', icon: '✅', t: 'Session live', d: 'Bot session is created, your number registered, and a session code returned — all in under a second.' },
          ].map((s, i) => (
            <div key={s.n} className={styles.howStep} style={{ animationDelay: `${i * 100}ms` }}>
              <div className={styles.howStepNum}>{s.n}</div>
              <div className={styles.howStepIcon}>{s.icon}</div>
              <h4 className={styles.howStepTitle}>{s.t}</h4>
              <p className={styles.howStepDesc}>{s.d}</p>
              {i < 2 && <div className={styles.howConnector} />}
            </div>
          ))}
        </div>
      </div>

      {/* ── API SNIPPET ── */}
      <div className={styles.apiSection}>
        <div className={styles.apiCard}>
          <div className={styles.apiLeft}>
            <span className={styles.howEyebrow}>For developers</span>
            <h3 className={styles.apiTitle}>Integrate in seconds</h3>
            <p className={styles.apiDesc}>
              Use the <code className={styles.inlineCode}>/dep/bybot</code> endpoint directly from your bot or backend.
              Pure JSON, no authentication required.
            </p>
            <div className={styles.apiTags}>
              <span>axios</span><span>fetch</span><span>curl</span><span>any HTTP client</span>
            </div>
          </div>
          <div className={styles.apiRight}>
            <div className={styles.codeBlock}>
              <div className={styles.codeBlockHeader}>
                <span className={styles.codeDot} style={{ background: '#ff5f56' }} />
                <span className={styles.codeDot} style={{ background: '#ffbd2e' }} />
                <span className={styles.codeDot} style={{ background: '#27c93f' }} />
                <span className={styles.codeBlockLang}>javascript</span>
              </div>
              <pre className={styles.codeBlockPre}>{`const res = await axios.get(
  '/dep/bybot?num=94XXXXXXXXX'
)

// Returns:
// { "type": "pair", "code": "WYH3RPJQ" }`}</pre>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
